import { Spinner, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type FormEvent, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { CheckboxField } from 'reactApp/sections/Nestle/CardForm/CheckboxField/CheckboxField';
import { InputField } from 'reactApp/sections/Nestle/CardForm/InputField/InputField';
import { useActiveSteps } from 'reactApp/sections/Nestle/hooks/useActiveSteps';
import { Button } from 'reactApp/ui/Button/Button';

import styles from './CardForm.css';

const items = ['Меня любят за просто так', 'Меня любят за вкусняшки со стола', 'Чешу за ушком каждый день', 'Держу пушистика в форме'];

interface CardForm {
    onSubmit: () => void;
    isLoading: boolean;
}

export const CardForm = memo<CardForm>(({ onSubmit, isLoading }) => {
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const activeSteps = useActiveSteps();

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            onSubmit();
        },
        [onSubmit]
    );

    const renderRequiredCheckboxes = () => (
        <>
            <CheckboxField field={'promo'} isRequired>
                Принимаю{' '}
                <a href="https://blog.mail.ru/pravila_pereklichka_po_klichkam/" className={styles.link} target="_blank" rel="noreferrer">
                    Правила Акции
                </a>
                . Акция с&nbsp;15.09.2025 до&nbsp;12.10.2025
            </CheckboxField>

            <Text
                className={classNames(styles.bottomWrap, styles.textNewsletter, {
                    [styles.textNewsletterPhone]: isMobile,
                })}
            >
                Последнее&nbsp;&mdash; подписаться на&nbsp;рассылку. В&nbsp;ней будет полезное для вас и&nbsp;вашего питомца
            </Text>

            <CheckboxField field={'nestleData'} isRequired>
                Даю согласие на&nbsp;обработку моих персональных данных ООО &laquo;Нестле Россия&raquo; в&nbsp;соответствии с&nbsp;политикой
                в&nbsp;отношении обработки персональных данных. Подробнее о&nbsp;
                <a className={styles.link} href="https://blog.mail.ru/consent_purina_cloud/" target="_blank" rel="noreferrer">
                    согласии
                </a>{' '}
                и&nbsp;
                <a
                    className={styles.link}
                    href="https://www.nestle.ru/sites/default/files/2024-09/data-policy-20240913.pdf"
                    target="_blank"
                    rel="noreferrer"
                >
                    политике
                </a>
                .
            </CheckboxField>

            <CheckboxField field={'newsletters'} isRequired>
                Даю согласие на&nbsp;получение{' '}
                <a className={styles.link} href="https://blog.mail.ru/soglasije_nestle_comm/" target="_blank" rel="noreferrer">
                    рекламно-информационной рассылки
                </a>{' '}
                по&nbsp;электронной почте в&nbsp;отношении брендов и&nbsp;мероприятий ООО &laquo;Нестле Россия&raquo;
            </CheckboxField>

            <CheckboxField field={'vkData'} isRequired>
                Даю согласие на&nbsp;получение{' '}
                <a className={styles.link} href="https://blog.mail.ru/soglasije_vk_comm/" target="_blank" rel="noreferrer">
                    рекламно-информационной рассылки от&nbsp;ООО &laquo;ВК&raquo;
                </a>
            </CheckboxField>
        </>
    );

    return (
        <div className={styles.mainContainer}>
            <div className={styles.contentWrapper}>
                <form onSubmit={handleSubmit} className={styles.formContainer}>
                    <div
                        className={classNames(styles.formOwner, {
                            [styles.formOwnerPhone]: isMobile,
                        })}
                    >
                        <Title
                            className={classNames(styles.title, {
                                [styles.titlePhone]: isMobile,
                            })}
                            level="2"
                        >
                            Заполните карточку хозяина
                        </Title>

                        <div
                            className={classNames(styles.container, {
                                [styles.containerPhone]: isMobile,
                            })}
                        >
                            {items.map((text, index) => {
                                return (
                                    <CheckboxField field={`item${index + 1}`} key={`item${index + 1}`}>
                                        {text}
                                    </CheckboxField>
                                );
                            })}
                        </div>

                        <div className={styles.formItemsBlock}>
                            <InputField name="name" placeholder="Введите своё имя" />
                            <InputField name="email" placeholder="Укажите свою почту" type="email" />
                        </div>

                        <CheckboxField field={'postInGroup'}>
                            Запостить карточку{' '}
                            <a className={styles.link} href="https://vk.com/mailru" target="_blank" rel="noreferrer">
                                в группе Mail
                            </a>{' '}
                            и на этой странице
                        </CheckboxField>

                        {isMobile && (
                            <>
                                <div className={styles.formCheckboxsBlockPhone}>{renderRequiredCheckboxes()}</div>

                                <Button
                                    className={classNames(styles.button, styles.buttonPhone, {
                                        [styles.buttonDisabledPhone]: activeSteps.length < 4 || isLoading,
                                    })}
                                    disabled={isLoading}
                                    type="submit"
                                >
                                    {isLoading && <Spinner size="small" className={styles.spinner} />}
                                    Участвовать в розыгрыше
                                </Button>
                            </>
                        )}
                    </div>

                    {!isMobile && (
                        <>
                            <div className={styles.formCheckboxsBlock}>{renderRequiredCheckboxes()}</div>

                            <div className={styles.buttonWrapper}>
                                <Button
                                    className={classNames(styles.button, {
                                        [styles.buttonDisabled]: activeSteps.length < 4 || isLoading,
                                    })}
                                    disabled={isLoading}
                                    type="submit"
                                >
                                    {isLoading && <Spinner size="small" className={styles.spinner} />}
                                    Участвовать в розыгрыше
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
});

CardForm.displayName = 'CardForm';
